import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PaginationComponent from '@/components/PaginationComponent';
import { Message, Select } from '@b-design/fusion';
import TableFilter from '@/components/TableFilter';
import ChannelTable from './component/channelTable';
import Button from '@/components/Button';
import Requests from '@/services/request';
import { getQueryStringArgs } from '@/utils/url-parse';
import { UrlEnum, filterKeys, status, Category } from '../../const.js';
import CalculateComponent from '@/components/CalculateComponent';
import $i18n from '@/i18n';
import store from '@/store';

import './index.less';

type Channel = Record<'channelCode' | 'channelName' | 'channelIcon', string>[];

const Channel: React.FC = () => {
  // 内容区域高度
  const [contentHeight, setContentHeight] = useState<number>(200);

  // 分页参数
  const [paginationValue, setPaginationValue] = useState<
    Record<'currentPage' | 'pageSize', number>
  >({
    currentPage: 1,
    pageSize: 10,
  });

  // channelList
  const [channelInfo, setChannelInfo] = useState<Channel>([]);
  const [channelValue, setChannelValue] = useState<string>('');

  const [filterValue, setFilterValue] = useState<Partial<Record<'status' | 'createName', string>>>(
    {},
  );

  // 列表数据和总条数
  const [tableData, setTableData] = useState({ totalCount: 0, data: [] });
  const [loading, setLoading] = useState<boolean>(false);

  const [categoryType, setCategoryType] = useState('');
  const [defaultChannel, setDefaultChannel] = useState('');
  const [categoryList, setCategoryList] = useState([]);

  const [spaceRoleState] = store.useModel('spaceRole');
  const spaceId = spaceRoleState?.spaceId;

  /**
   * 初始化表单数据请求
   * @param params 分页参数
   */
  const initData = (
    params = {
      currentPage: 1,
      pageSize: 10,
    },
  ) => {
    setLoading(true);
    Requests.get('/rest/intercnn/app/publish/query', { ...params, appName: encodeURIComponent(params?.appName || '') }).then((data) => {
      if (data) {
        const { total = 0, list = [] } = data;
        setTableData({
          totalCount: total,
          data: list,
        });
      }
      setLoading(false);
    });
  };

  /**
   * 条件查询处理
   * @param filterParams 询筛选参数
   */
  const searchHandler = (filterParams) => {
    setPaginationValue((pre) => ({
      ...pre,
      currentPage: 1,
    }));
    setFilterValue((pre) => ({
      status: pre?.status,
      createName: pre?.createName,
      ...filterParams,
    }));
  };

  /**
   * 翻页查询处理
   * @param current 当前页码
   * @param pageSize 每页数据量
   */
  const pageChangeHandler = (current: number, pageSize: number) => {
    setPaginationValue({
      currentPage: current,
      pageSize,
    });
  };

  // 获取channelCode 和 miniapp dataSource
  const queryChannel = useCallback(
    (params = { spaceId, currentPage: 1, pageSize: 10000 }) => {
      if (!spaceId) {
        return;
      };
      setLoading(true);
      Requests.get('/rest/intercnn/publishChannel/pageQuery', {
        ...params,
        belongSpaceId: spaceRoleState?.belongSpaceId || '',
      }).then((data) => {
        if (data) {
          const { list = [] } = data;
          setDefaultChannel(list[0]?.innerCode);
          setChannelInfo(
            list?.map((channel) => ({
              channelCode: channel?.innerCode,
              channelName: channel?.name,
              channelIcon: channel?.iconPublicUrl,
            })),
          );
          setLoading(false);
        }
      });
    },
    [spaceId],
  );

  useEffect(() => {
    queryChannel();
    if (!spaceId) {
      return;
    };
    Requests.post('/rest/intercnn/space/category/list', { spaceId }).then((res) => {
      const data = res || [];
      data.push({
        id: -1,
        name: {
          zh_CN: $i18n.get({
            id: 'aopf.component.miniAppOnlineStatus.Uncategorized',
            dm: '未分类',
          }),
          en_US: 'Unfiled',
        },
      });
      setCategoryList(data);
    });
  }, []);

  useEffect(() => {
    if (channelInfo?.length) {
      const params = {
        ...paginationValue,
        // channelCode:
        //   channelValue || channelInfo?.map((channel) => channel?.channelCode)?.join(','),
        channelCode: channelValue || defaultChannel,
        ...filterValue,
        status: filterValue?.status || status,
        categroyId: categoryType,
      };
      initData(params);
    }
  }, [paginationValue, channelValue, filterValue, channelInfo, defaultChannel, categoryType]);

  const onlineOrOffline = async (params, type) => {
    const data = await Requests.post(UrlEnum?.[type], { ...params });
    if (data) {
      Message.success({
        title: $i18n.get({
          id: 'aopf.pages.channel.channelList.message.success',
          dm: '修改成功',
        }),
      });
      const params = {
        ...paginationValue,
        channelCode: channelValue || defaultChannel,
        ...filterValue,
        status: filterValue?.status || status,
        categroyId: categoryType,
      };
      initData(params);
    }
  };

  return (
    <div className="global-filter-list">
      <div className="list-content channel-list">
        <div className="content-btns-filter">
          <Select
            className="filter-channel-select"
            popupProps={{ v2: true }}
            popupClassName="b-design-select"
            onChange={(value) => {
              setChannelValue(value);
              setPaginationValue((pre) => ({
                ...pre,
                currentPage: 1,
              }));
            }}
            style={{ width: 350, marginRight: 12 }}
            defaultValue={defaultChannel}
            key={defaultChannel}
          >
            {channelInfo.map((val) => {
              return (
                <Select.Option
                  value={val.channelCode}
                  key={val.channelCode}
                  className="btn-channel"
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img src={val?.channelIcon} style={{ marginRight: '8px', flex: '0 0 auto' }} />
                    {val.channelName}
                  </div>
                </Select.Option>
              );
            })}
          </Select>

          <TableFilter filterKeys={filterKeys} filterHandle={searchHandler} />
          <div className="category-select">
            <div className="category-label">
              {$i18n.get({
                id: 'aopf.pages.miniAppStore.select.category',
                dm: '类目',
              })}
            </div>
            <Select
              className="filter-channel-select"
              popupProps={{ v2: true }}
              popupClassName="b-design-select"
              onChange={(value) => {
                setCategoryType(value);
              }}
              style={{ width: 150 }}
              hasClear
            >
              {categoryList.map((val) => {
                return (
                  <Select.Option value={val.id} key={val.id} className="btn-channel">
                    {$i18n.language === 'zh-CN' ? val?.name?.zh_CN : val?.name?.en_US}
                  </Select.Option>
                );
              })}
            </Select>
          </div>
        </div>
        <ChannelTable
          contentHeight={contentHeight}
          dataSource={tableData?.data}
          onlineOrOffline={onlineOrOffline}
          setFilterValue={() => {
            setPaginationValue((pre) => ({
              ...pre,
              currentPage: 1,
            }));
            setFilterValue((pre) => ({
              ...pre,
            }));
          }}
          spaceId={spaceId || ''}
          loading={loading}
        />

        <CalculateComponent
          otherWidth={280}
          otherHeight={88 + 48 + 24 + 56 + 56}
          windowChange={(_width, height) => {
            setContentHeight(height);
          }}
        />

        <PaginationComponent
          total={tableData.totalCount}
          current={paginationValue.currentPage}
          pageSize={paginationValue.pageSize}
          onPageChange={pageChangeHandler}
        />
      </div>
    </div>
  );
};
export default Channel;
